package com.evoapps.evoshift.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.saveable.rememberSaveable
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.of
import kotlinx.datetime.YearMonth

/**
 * The workplace's months as the shell knows them: [rosters] belong to [workplaceId]; [loading]
 * while another workplace's are on their way.
 */
class KnownRosters(val workplaceId: String?, val rosters: List<Roster>, val loading: Boolean)

/**
 * SH-89: a screen of one roster (its id in the route: links, process restore, one view model per
 * month) that follows the app-wide month and workplace (docs/design/top-bar.md):
 *
 * 1. the first time the entry shows, its roster sets the selection (a link or a notification
 *    sets the month and the workplace);
 * 2. afterwards a different selection replaces the entry by that month's roster ([onReplace]);
 * 3. a month without a roster shows [missing] in place, with the same bar.
 */
@Composable
fun RosterMonthHost(
    entryRosterId: String,
    selection: WorkplaceMonthSelection,
    known: KnownRosters,
    onReplace: (Roster) -> Unit,
    missing: @Composable (YearMonth) -> Unit,
    content: @Composable () -> Unit
) {
    // Once per back stack entry (kept across tabs and process restore), before reading the selection.
    rememberSaveable(entryRosterId) { selection.openRoster(entryRosterId); true }
    val month by selection.month.collectAsState()
    val workplaceId by selection.workplaceId.collectAsState()

    val entryMonth = WorkplaceMonthSelection.monthOf(entryRosterId)
    val entryWorkplace = RosterIds.workplaceIdOf(entryRosterId)
    if (month == entryMonth && workplaceId == entryWorkplace) {
        content()
        return
    }
    // The selected workplace's months not known yet: the entry stays until they are.
    if (known.loading || known.workplaceId != workplaceId) {
        content()
        return
    }
    val target = known.rosters.of(month)
    if (target != null) {
        LaunchedEffect(target.id) { onReplace(target) }
        content()
    } else {
        missing(month)
    }
}
